import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import type { useVaultHolderEventsQuery } from '@/queries/vaultHolderEvents.query'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatDate } from '@vaultsfyi/common/utils'
import { formatUnits } from 'viem'

const FORMAT_CONFIG = { maximumFractionDigits: 4 } as const

interface ActivityColumn {
  alignRight?: boolean
  label: string
}

const COLUMNS: readonly ActivityColumn[] = [
  { label: 'date & time' },
  { label: 'activity' },
  { label: 'amount', alignRight: true },
  { label: 'asset', alignRight: true },
] as const

interface PositionActivityProps {
  asset: Vault['asset']
  events: ReturnType<typeof useVaultHolderEventsQuery>['events']
}

export const PositionActivity = ({ asset, events }: PositionActivityProps) => (
  <section className="flex flex-col gap-3">
    <h4 className="font-medium text-heading-foreground md:text-lg">Activity</h4>
    <div className="overflow-hidden rounded-2xl border">
      <ScrollArea className="w-full">
        <Table className="min-w-[40rem]">
          <TableHeader>
            <TableRow variant="header">
              {COLUMNS.map(({ label, alignRight }) => (
                <TableHead key={label} className={cn('whitespace-nowrap capitalize', alignRight && 'text-right')}>
                  {label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} className="h-16 text-center">
                  This wallet has no actions on this vault
                </TableCell>
              </TableRow>
            ) : (
              events.map((event, index) => {
                const isDeposit = event.eventType === 'deposit'
                const amount = Number(formatUnits(BigInt(event.eventAssetAmount.native), asset.decimals))
                const valueClassName = isDeposit ? 'text-success' : 'text-destructive'

                return (
                  <TableRow key={`${event.timestamp}-${index}`}>
                    <TableCell className="h-16 whitespace-nowrap px-3 py-4 tabular-nums">
                      {formatDate({
                        timestamp: event.timestamp * SECOND_IN_MILLISECONDS,
                        showTime: true,
                      })}
                    </TableCell>
                    <TableCell className="h-16 px-3 py-4">
                      <span className={cn('inline-block font-medium capitalize', valueClassName)}>
                        {event.eventType}
                      </span>
                    </TableCell>
                    <TableCell className={cn('h-16 px-3 py-4 text-right tabular-nums', valueClassName)}>
                      {isDeposit ? '+' : '-'}
                      {Intl.NumberFormat('en-US', FORMAT_CONFIG).format(Math.abs(amount))}
                    </TableCell>
                    <TableCell className="h-16 px-3 py-4 text-right text-caption-foreground">{asset.symbol}</TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  </section>
)
